import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Table,
  Input,
  Card,
  Typography,
  Tag,
  Tooltip,
  Spin,
  Space,
  Divider,
  message,
  Badge,
  Button
} from 'antd';
import moment from 'moment';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BookOutlined,
  FileTextOutlined,
  SyncOutlined,
  CloseCircleOutlined,
    PauseCircleOutlined,
    ExclamationCircleOutlined,
    ReconciliationOutlined,
    QuestionCircleOutlined,
    FileExcelOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Search } = Input;

const ClassChange = () => {
  const { i18n, t } = useTranslation();
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment().endOf('month')
  ]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    showSizeChanger: true,
    pageSizeOptions: ['100', '150', '200'],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  
  const API_URL = process.env.REACT_APP_API_URL;

  const formatTime = (timeString) => {
    if (!timeString) return null;
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    } catch (error) {
      return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      return new Intl.DateTimeFormat(i18n.language, options).format(date);
    } catch {
      return null;
    }
  };

  const formatDateTime = (dateTimeString) => {
    const dateTime = new Date(dateTimeString);
    const options = { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Intl.DateTimeFormat(i18n.language, options).format(dateTime);
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    try {
      const start = new Date(`1970-01-01T${startTime}Z`);
      const end = new Date(`1970-01-01T${endTime}Z`);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      const diff = (end - start) / (1000 * 60);

      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;

      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hr')}`;
    } catch (error) {
      return null;
    }
  };

  const getChangeReasonTag = (reason) => {
    const reasonMap = {
      'admin_adjust': { color: 'blue', icon: <SyncOutlined />, text: 'Admin' },
      'cancelled_by_student': { color: 'red', icon: <CloseCircleOutlined />, text: 'CL by S.' },
      'cancelled_by_teacher': { color: 'orange', icon: <CloseCircleOutlined />, text: 'CL by T.' },
      'class_on_hold_by_student': { color: 'geekblue', icon: <PauseCircleOutlined />, text: 'On hold by S.' },
      'class_on_hold_by_school': { color: 'green', icon: <PauseCircleOutlined />, text: 'On hold by P.' },
      'cancel_same_day': { color: 'volcano', icon: <ExclamationCircleOutlined />, text: 'Cancel Same Day' },
      'make_up': { color: 'purple', icon: <ReconciliationOutlined />, text: 'Make Up' },
      'class_added': { color: 'green', icon: <CalendarOutlined />, text: 'Class Added' },
      'others': { color: 'default', icon: <QuestionCircleOutlined />, text: 'Others' }
      
    };

    const config = reasonMap[reason] || { color: 'default', text: reason };
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const fetchClassChangeLogs = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].clone().add(1, 'day').format('YYYY-MM-DD');

      const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: { start_date: startDate, end_date: endDate }
      });
      setClassChangeLogs(response.data);
      setFilteredLogs(response.data);
      setSearchTerm('');
      setPagination(prev => ({
        ...prev,
        current: 1,
        total: response.data.length
      }));
    } catch (error) {
      console.error('Error fetching class change logs:', error);
      message.error('Failed to load class change logs');
    } finally {
      setLoading(false);
    }
  }, [API_URL, dateRange]);

  useEffect(() => {
    fetchClassChangeLogs();
  }, [fetchClassChangeLogs]);

  const handleSearch = (value) => {
    const term = value.toLowerCase();
    setSearchTerm(term);
    const filtered = classChangeLogs.filter(
      (log) =>
        log.classid?.toString().includes(term) || 
        log.subject_name?.toLowerCase().includes(term) ||
        log.class_code?.toLowerCase().includes(term) ||
        log.nickname?.toLowerCase().includes(term)
    );
    setFilteredLogs(filtered);
    setPagination(prev => ({
      ...prev,
      current: 1,
      total: filtered.length
    }));
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const prepareCSVData = () => {
    return filteredLogs.map(log => ({
      'Class ID': log.classid,
      'Class Code': log.class_code,
      'Subject': log.subject_name,
      'Teacher': log.nickname,
      'Original Date': formatDate(log.originaldate) || '-',
      'Original Time': formatTime(log.originalstarttime) && formatTime(log.originalendtime) ? `${formatTime(log.originalstarttime)} - ${formatTime(log.originalendtime)}` : '-',
      'Original Hours': calculateDuration(log.originalstarttime, log.originalendtime) || '-',
      'New Date': formatDate(log.newdate) || '-',
      'New Time': formatTime(log.newstarttime) && formatTime(log.newendtime) ? `${formatTime(log.newstarttime)} - ${formatTime(log.newendtime)}` : '-',
      'New Hours': calculateDuration(log.newstarttime, log.newendtime) || '-',
      'Change Reason': log.changereason,
      'Note': log.note || '',
      'Updated By': log.updateby,
      'Updated At': formatDateTime(log.updated_at)
    }));
  };

  const columns = [
    {
      title: t('classcode'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 120,
      fixed: 'left',
      render: code => (
        <Badge status="processing" text={code} />
      )
    },
    {
      title: t('id'),
      dataIndex: 'classid',
      key: 'classid',
      width: 80,
    },
    {
      title: t('subject'),
      dataIndex: 'subject_name',
      key: 'subject_name',
      width: 150,
      render: text => (
        <Tooltip title={text}>
          <Space>
            <BookOutlined />
            <Text ellipsis style={{ maxWidth: 120 }}>{text}</Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: t('teacher'),
      dataIndex: 'nickname',
      key: 'nickname',
      width: 120,
      render: name => (
        <Space>
          <UserOutlined />
          <Text>{name}</Text>
        </Space>
      )
    },
 {
  title: t('originaldate'),
  dataIndex: 'originaldate',
  key: 'originaldate',
  width: 150,
  render: (date, record) => {
    const formatted = formatDate(date);
    const start = formatTime(record.originalstarttime);
    const end = formatTime(record.originalendtime);
    const duration = calculateDuration(record.originalstarttime, record.originalendtime);

    const dateChanged = formatDate(record.newdate) !== formatted;
    const timeChanged = formatTime(record.newstarttime) !== start || formatTime(record.newendtime) !== end;
    const durationChanged = calculateDuration(record.newstarttime, record.newendtime) !== duration;

    return (
      <Space direction="vertical" size={0}>
        {formatted ? (
          <Space>
            <CalendarOutlined style={dateChanged ? { color: '#ff4d4f' } : {}} />
            <Text style={dateChanged ? { color: '#ff4d4f', fontWeight: 600 } : {}}>{formatted}</Text>
          </Space>
        ) : <Text type="secondary">-</Text>}
        {start && end ? (
          <Space>
            <ClockCircleOutlined style={timeChanged ? { color: '#ff4d4f' } : {}} />
            <Text style={timeChanged ? { color: '#ff4d4f', fontWeight: 600 } : {}}>{start} - {end}</Text>
          </Space>
        ) : <Text type="secondary">-</Text>}
        {duration
          ? <Text type={durationChanged ? undefined : 'secondary'} style={durationChanged ? { color: '#ff4d4f', fontWeight: 600 } : {}}>{duration}</Text>
          : <Text type="secondary">-</Text>}
      </Space>
    );
  }
},

{
  title: t('newdate'),
  dataIndex: 'newdate',
  key: 'newdate',
  width: 150,
  render: (date, record) => {
    const formatted = formatDate(date);
    const start = formatTime(record.newstarttime);
    const end = formatTime(record.newendtime);
    const duration = calculateDuration(record.newstarttime, record.newendtime);

    const dateChanged = formatDate(record.originaldate) !== formatted;
    const timeChanged = formatTime(record.originalstarttime) !== start || formatTime(record.originalendtime) !== end;
    const durationChanged = calculateDuration(record.originalstarttime, record.originalendtime) !== duration;

    return (
      <Space direction="vertical" size={0}>
        {formatted ? (
          <Space>
            <CalendarOutlined style={dateChanged ? { color: '#ff4d4f' } : {}} />
            <Text style={dateChanged ? { color: '#ff4d4f', fontWeight: 600 } : {}}>{formatted}</Text>
          </Space>
        ) : <Text type="secondary">-</Text>}
        {start && end ? (
          <Space>
            <ClockCircleOutlined style={timeChanged ? { color: '#ff4d4f' } : {}} />
            <Text style={timeChanged ? { color: '#ff4d4f', fontWeight: 600 } : {}}>{start} - {end}</Text>
          </Space>
        ) : <Text type="secondary">-</Text>}
        {duration
          ? <Text type={durationChanged ? undefined : 'secondary'} style={durationChanged ? { color: '#ff4d4f', fontWeight: 600 } : {}}>{duration}</Text>
          : <Text type="secondary">-</Text>}
      </Space>
    );
  }
},
 {
      title: t('changereason'),
      dataIndex: 'changereason',
      key: 'changereason',
      width: 130,
      render: reason => getChangeReasonTag(reason)
    },
    {
      title: t('admin'),
      dataIndex: 'updateby',
      key: 'updateby',
      width: 120,
    },
    {
      title: t('updateat'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: date => formatDateTime(date)
    },
  ];

  return (
    <div className="class-change-container">      
      <Card className="search-card">
  <Space direction="vertical" style={{ width: '100%' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="date"
          value={dateRange[0].format('YYYY-MM-DD')}
          onChange={(e) => setDateRange([moment(e.target.value), dateRange[1]])}
          style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 14 }}
        />
        <span style={{ color: '#999' }}>to</span>
        <input
          type="date"
          value={dateRange[1].format('YYYY-MM-DD')}
          onChange={(e) => setDateRange([dateRange[0], moment(e.target.value)])}
          style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 14 }}
        />
      </div>
      <Search
        placeholder={t('Search by Class ID, Code, Subject or Teacher')}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        onSearch={handleSearch}
        enterButton
        allowClear
        style={{ flex: 1 }}
      />
      <CSVLink
        data={prepareCSVData()}
        filename={`class-changes-${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.csv`}
        className="ant-btn ant-btn-primary"
      >
        <Button type="primary" icon={<FileExcelOutlined />}>
              {t("Export CSV")}
            </Button>
      </CSVLink>
    </div>
    <Text type="secondary">
      {filteredLogs.length} {t('records found')} — {dateRange[0].format('DD MMM YYYY')} to {dateRange[1].format('DD MMM YYYY')}
    </Text>
  </Space>
</Card>
      
      <Divider />
      
      <Spin spinning={loading} tip={t('Loading logs...')}>
        <Table
          columns={columns}
          dataSource={filteredLogs}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered
        />
      </Spin>
    </div>
  );
};

export default withRoleAccess(ClassChange, ['admin', 'superadmin']);